import { Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TakeUntilDestroy } from '@app/shared/decorators/take-until-destroy.decorator';
import { LimitedTimeEvent } from '@app/shared/models/limitedTimeEvent.model';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { ShopOffersItem } from '@app/shared/models/shop-offers.model';
import { LoggerService } from '@app/shared/services/logger.service';
import { EventManager } from '@app/shared/utils/event-manager';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { LimitedTimeEventsLayoutComponent } from '@app/shared/components/liveops/limited-time-events-layout/limited-time-events-layout.component';
import { BoxComponent } from '@app/shared/components/liveops/box/box.component';
import { AnimatedTimerComponent } from '@app/shared/components/animated-timer/animated-timer.component';
import { StampProgressComponent } from '@app/shared/components/stamp-progress/stamp-progress.component';
import { DisplayComponent } from '@app/shared/components/display/display.component';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { SharedModule } from '@app/shared/shared.module';
import { EventsRepository } from '@app/shared/state/events/events.repository';
import { ShopRatesRepository } from '@app/shared/state/shop-rates/shop-rates.repository';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-slotcard-modal',
  templateUrl: './slotcard.component.html',
  styleUrls: ['./slotcard.component.scss'],
  standalone: true,
  imports: [
    LimitedTimeEventsLayoutComponent,
    BoxComponent,
    AnimatedTimerComponent,
    StampProgressComponent,
    DisplayComponent,
    ButtonComponent,
    SharedModule,
  ],
})
export class SlotcardModalComponent implements OnInit {
  componentDestroy: () => Observable<unknown>;
  private readonly eventManager = inject(EventManager);
  private readonly eventsRepo = inject(EventsRepository);
  private readonly shopRatesRepo = inject(ShopRatesRepository);
  private readonly translateService = inject(TranslateService);
  public readonly logger = inject(LoggerService);
  protected readonly slotcardModalData = inject<Modal>(MAT_DIALOG_DATA);

  NonPersistedModalName = NonPersistedModalName;
  event: LimitedTimeEvent;
  percentExtra: number;
  slotcardAveragePurchases: number = 0;
  slotcardShopItem: ShopOffersItem;
  slotcardMinPurchase: string;
  slotcardCurrencySymbol: string;
  lang: string;

  ngOnInit(): void {
    const eventId = this.slotcardModalData.data.eventId
      ? this.slotcardModalData.data.eventId
      : this.slotcardModalData.data.properties.eventId;
    this.event = this.eventsRepo.getEventById(eventId);
    this.lang = this.translateService.currentLang;

    this.calculateMinPurchase();
    this.calculateAveragePurchases();
  }

  closeModal(): void {
    this.eventManager.closeModal(new Modal(this.slotcardModalData.id, ModalType.View, this.event));
  }

  openShop(): void {
    this.closeModal();

    this.eventManager.openModal(
      new Modal(NonPersistedModalName.ShopModalComponent, ModalType.View, {
        tab: 'left',
      })
    );
  }

  private calculateMinPurchase(): void {
    this.slotcardShopItem = this.shopRatesRepo
      .getShopRates()
      .Coins?.find((rate) => rate.sku === this.event.properties.sku);
    this.slotcardMinPurchase = this.slotcardShopItem ? this.slotcardShopItem.amount : '0';
    this.slotcardCurrencySymbol = this.slotcardShopItem ? this.slotcardShopItem.currencySymbol : '';
  }

  private calculateAveragePurchases(): void {
    if (this.event.properties.purchases.length <= 0) {
      return;
    }

    let averagePurchases = 0;

    this.event.properties.purchases.forEach((purchase) => {
      averagePurchases += purchase;
    });

    averagePurchases = averagePurchases / this.event.properties.purchases.length;
    this.slotcardAveragePurchases =
      averagePurchases + (averagePurchases * this.event.properties.extrarewardpercentage) / 100;
  }
}
